import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { BrandLockup, BrandMark } from "@/components/Brand";
import { Wallet, LogOut, Bell, LayoutDashboard, Users, FileText, Settings, ShieldCheck, ClipboardList, Menu, HandCoins } from "lucide-react";

const navByRole = {
  borrower: [
    { to: "/borrower", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/borrower/apply", label: "New Application", icon: FileText },
    { to: "/borrower/loans", label: "My Loans", icon: Wallet },
    { to: "/borrower/notifications", label: "Notifications", icon: Bell },
  ],
  reviewer: [
    { to: "/reviewer", label: "Queue", icon: ClipboardList, end: true },
    { to: "/admin/loans", label: "Loan Book", icon: Wallet },
    { to: "/admin/collections", label: "Collections", icon: HandCoins },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/applications", label: "Applications", icon: FileText },
    { to: "/admin/loans", label: "Loan Book", icon: Wallet },
    { to: "/admin/collections", label: "Collections", icon: HandCoins },
    { to: "/admin/compliance", label: "Compliance", icon: ShieldCheck },
    { to: "/admin/products", label: "Loan Products", icon: Wallet },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/templates", label: "Templates", icon: Settings },
    { to: "/admin/audit", label: "Audit Logs", icon: ShieldCheck },
  ],
};

function NavItems({ items, onClick }) {
  return (
    <nav className="bg-white rounded-xl border border-slate-200 p-2" data-testid="sidebar-nav">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onClick}
          data-testid={`nav-${it.label.toLowerCase().replaceAll(' ', '-')}`}
          className={({ isActive }) =>
            `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium mb-1 transition-colors ${
              isActive ? "bg-amber-50 text-[#0A1F44] border border-amber-200/60" : "text-slate-600 hover:bg-slate-50"
            }`
          }
        >
          <it.icon className="h-4 w-4" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const items = navByRole[user?.role] || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9 -ml-1" data-testid="mobile-menu-btn" aria-label="Menu">
                  <Menu className="h-5 w-5"/>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-4">
                <Link to="/" onClick={()=>setDrawerOpen(false)} className="flex items-center mb-4" data-testid="drawer-logo">
                  <BrandLockup variant="dark" />
                </Link>
                <NavItems items={items} onClick={()=>setDrawerOpen(false)} />
                <div className="mt-3 px-2 text-xs text-slate-500 truncate">{user?.email}</div>
              </SheetContent>
            </Sheet>

            <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="header-logo">
              <BrandMark size={40} />
              <div className="hidden sm:block leading-tight">
                <div className="font-bold text-slate-900 tracking-tight text-sm" style={{fontFamily:'Outfit', letterSpacing:'0.02em'}}>MABUHAY</div>
                <div className="text-[9px] font-semibold tracking-[0.22em] uppercase text-amber-700" style={{fontFamily:'Outfit'}}>Capital Lending</div>
              </div>
              <span className="sm:hidden font-bold text-slate-900 tracking-tight text-sm" style={{fontFamily:'Outfit'}}>MABUHAY</span>
              <span className="hidden sm:inline-block text-xs ml-2 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 capitalize" data-testid="header-role">{user?.role}</span>
            </Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span className="hidden md:inline text-sm text-slate-600 truncate max-w-[12rem]" data-testid="header-user-email">{user?.email}</span>
            <Button variant="outline" size="sm" data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }}>
              <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        <aside className="hidden lg:block lg:col-span-3">
          <NavItems items={items} />
        </aside>
        <main className="lg:col-span-9 min-w-0">{children}</main>
      </div>
    </div>
  );
}
